import React, { useState, useEffect, useRef } from 'react';
import loadable from '@loadable/component';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { getListingsById } from '../../ducks/marketplaceData.duck';
// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  Heading,
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  SoldByPill,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';

import css from './ListingPage.module.css';
import { isArrayLength } from '../../util/genericHelpers.js';
import BreadCrumbs from './BreadCrumbs.js';
import SimilarListingsSection from './SimilarListingsSection.js';
import { withViewport } from '../../util/uiHelpers.js';
import {
  formatDisplayValue,
  getBaseListingDetails,
  normalizeComparableValue,
} from './listingDetails.js';
import SectionAuthorMaybe from './SectionAuthorMaybe.js';
import SectionShopNewBrand from './SectionShopNewBrand';
import useShopNewBrand, { isShopNewListing } from './useShopNewBrand';
import CustomListingFields from './CustomListingFields';
import { isRetreatListingType } from '../../config/configRetreat';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
const RetreatListingPage = loadable(() =>
  import(/* webpackChunkName: "RetreatListingPage" */ './RetreatListingPage')
);

const { UUID } = sdkTypes;

const getListingSpecs = (publicData, intl) => {
  if (!publicData || typeof publicData !== 'object') {
    return [];
  }

  const { listingType, unitType, tags, shippingEnabled } = publicData;
  const specs = [...getBaseListingDetails(publicData)];

  const maybeListingType = formatDisplayValue(listingType);
  const normalizedListingType = normalizeComparableValue(maybeListingType);
  if (maybeListingType && normalizedListingType !== 'sell new products') {
    specs.push({ key: 'listingType', label: 'Listing type', value: maybeListingType });
  }

  const maybeUnitType = formatDisplayValue(unitType);
  const normalizedUnitType = normalizeComparableValue(maybeUnitType);
  if (maybeUnitType && normalizedUnitType !== 'item') {
    specs.push({ key: 'unitType', label: 'Unit type', value: maybeUnitType });
  }

  if (typeof shippingEnabled === 'boolean' && shippingEnabled === false) {
    specs.push({
      key: 'shippingEnabled',
      label: 'Shipping enabled',
      value: intl.formatMessage({
        id: shippingEnabled ? 'SearchPage.detailYes' : 'SearchPage.detailNo',
      }),
    });
  }

  const tagValues = Array.isArray(tags)
    ? tags.map(tag => formatDisplayValue(tag)).filter(Boolean)
    : [];
  if (tagValues.length > 0) {
    specs.push({ key: 'tags', label: 'Tags', value: tagValues.join(', ') });
  }

  return specs;
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const sectionRef = useRef(null);

  const [isFixed, setIsFixed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();

      // ---- SHOW ----
      if (rect.bottom <= window.innerHeight && !isVisible) {
        setIsFixed(true); // keep position fixed
        setIsVisible(true); // fade in & slide up
      }

      // ---- START HIDE ----
      if (rect.top >= 0 && isVisible) {
        setIsVisible(false); // fade out & slide down

        // remove fixed AFTER animation completes
        setTimeout(() => {
          setIsFixed(false);
        }, 300); // match CSS transition time
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isVisible]);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    similarListings,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));
  const shopNewBrand = useShopNewBrand(currentListing);

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  const listingSpecs = getListingSpecs(publicData, intl);
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    config,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  if (isRetreatListingType(listingType))
    return (
      <RetreatListingPage
        {...props}
        listing={currentListing}
        isOwnListing={isOwnListing}
        onContactUser={onContactUser}
        onSubmitInquiry={onSubmitInquiry}
        authorDisplayName={authorDisplayName}
        isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
        onCloseInquiryModal={() => setInquiryModalOpen(false)}
        editParams={{
          id: listingId.uuid,
          slug: listingSlug,
          type: listingPathParamType,
          tab: 'details',
        }}
      />
    );
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );

  const images = currentListing.images;

  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.listingPageWrapper}>
          <div className={css.fixedWidthContainer}>
            <BreadCrumbs listingTitle={title} />
            <div className={css.contentWrapperForProductLayout}>
              <div className={css.mainContentWrapper} ref={sectionRef}>
                <div className={css.mainColumnForProductLayout}>
                  {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
                    <ActionBarMaybe
                      className={css.actionBarForProductLayout}
                      isOwnListing={isOwnListing}
                      listing={currentListing}
                      showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                      currentUser={currentUser}
                    />
                  ) : null}
                  {mounted && currentListing.id ? (
                    <ActionBarMaybe
                      className={css.actionBarForProductLayout}
                      isOwnListing={isOwnListing}
                      listing={currentListing}
                      currentUser={currentUser}
                      editParams={{
                        id: listingId.uuid,
                        slug: listingSlug,
                        type: listingPathParamType,
                        tab: listingTab,
                      }}
                    />
                  ) : null}
                  {/* {showListingImage && (
              <SectionGallery
                listing={currentListing}
                variantPrefix={config.layout.listingImage.variantPrefix}
              />
            )} */}

                  {/* all images in array */}
                  {images.map((img, index) => {
                    const variants = img.attributes.variants;
                    const full =
                      variants['scaled-xlarge'] ||
                      variants['listing-card-6x'] ||
                      variants['listing-card-4x'] ||
                      variants['scaled-large'];

                    return (
                      <div key={img?.id?.uuid} className={css.productImageWrapper}>
                        {index === 0 ? (
                          <SoldByPill
                            author={currentListing.author}
                            listing={currentListing}
                            containerClassName={css.productImageSoldByContainer}
                          />
                        ) : null}
                        <img
                          className={css.productImage}
                          src={full?.url}
                          alt={title || 'Listing image'}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className={css.orderColumnForProductLayout}>
                  <SectionShopNewBrand listing={currentListing} brand={shopNewBrand} compact />
                  <OrderPanel
                    className={classNames(css.productOrderPanel, {
                      [css.imagesEnabled]: showListingImage,
                    })}
                    listing={currentListing}
                    isOwnListing={isOwnListing}
                    onSubmit={handleOrderSubmit}
                    authorLink={
                      <NamedLink
                        className={css.authorNameLink}
                        name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                        params={params}
                        to={{ hash: '#author' }}
                      >
                        {authorDisplayName}
                      </NamedLink>
                    }
                    title={
                      <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                    }
                    titleDesktop={
                      <H4 as="h1" className={css.orderPanelTitle}>
                        <FormattedMessage
                          id="ListingPage.orderTitle"
                          values={{ title: richTitle }}
                        />
                      </H4>
                    }
                    payoutDetailsWarning={payoutDetailsWarning}
                    author={ensuredAuthor}
                    onManageDisableScrolling={onManageDisableScrolling}
                    onContactUser={onContactUser}
                    {...restOfProps}
                    validListingTypes={config.listing.listingTypes}
                    marketplaceCurrency={config.currency}
                    dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                    marketplaceName={config.marketplaceName}
                    showListingImage={showListingImage}
                  />
                  {/* <div className={css.actionBtns}>
                    <PrimaryButton className={css.addCartBtn}>Add to cart</PrimaryButton>
                    <SecondaryButton className={css.addCartBtn}>
                      <Icons name="gift" /> drop a hint
                    </SecondaryButton>
                  </div> */}
                  {/* <div className={css.quantitySec}>
                    <p>Quantity</p>
                    <div className={css.stock}>
                      <InlineTextButton type="button">
                        <Icons name="minus" />
                      </InlineTextButton>
                      <span className={css.stockCount}>1</span>
                      <InlineTextButton type="button">
                        <Icons name="plus" />
                      </InlineTextButton>
                    </div>
                  </div> */}
                  <SectionTextMaybe text={description} showAsIngress />
                  {listingSpecs.length > 0 ? (
                    <section className={css.keyFeatures}>
                      <Heading as="h2" rootClassName={css.sectionHeading}>
                        <FormattedMessage id="ListingPage.detailsTitle" />
                      </Heading>
                      <ul>
                        {listingSpecs.map(spec => (
                          <li key={spec.key}>
                            <span className={css.detailLabel}>{spec.label}:</span>{' '}
                            <span className={css.detailValue}>{spec.value}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ) : null}
                  {/*<CustomListingFields
                    publicData={publicData}
                    metadata={metadata}
                    listingFieldConfigs={listingConfig.listingFields}
                    categoryConfiguration={config.categoryConfiguration}
                    intl={intl}
                  />
                  <SectionMapMaybe
                    geolocation={geolocation}
                    publicData={publicData}
                    listingId={currentListing.id}
                    mapsConfig={config.maps}
                  />
                  <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} /> */}
                  {isShopNewListing(currentListing) ? (
                    <CustomListingFields
                      publicData={publicData}
                      metadata={metadata}
                      listingFieldConfigs={listingConfig.listingFields.filter(
                        field =>
                          (field.scope || 'public') === 'public' &&
                          field.key !== 'affiliateLink' &&
                          !listingSpecs.some(spec => spec.key === field.key)
                      )}
                      categoryConfiguration={config.categoryConfiguration}
                      intl={intl}
                      includeBaseDetails={false}
                    />
                  ) : null}
                  <SectionAuthorMaybe
                    shopNewBrand={shopNewBrand}
                    title={title}
                    listing={currentListing}
                    authorDisplayName={authorDisplayName}
                    onContactUser={onContactUser}
                    isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                    onCloseInquiryModal={() => setInquiryModalOpen(false)}
                    sendInquiryError={sendInquiryError}
                    sendInquiryInProgress={sendInquiryInProgress}
                    onSubmitInquiry={onSubmitInquiry}
                    currentUser={currentUser}
                    onManageDisableScrolling={onManageDisableScrolling}
                  />
                </div>
              </div>
              {/* {!isMobileLayout && (
                <div
                  className={classNames(
                    isFixed ? css.fixed : null,
                    isVisible ? css.show : css.hide,
                    css.stickyOrderForm
                  )}
                >
                  <div className={css.stickyOrderFormStart}>
                    <div className={css.productImg}>
                      <img
                        src={
                          images?.[0]?.attributes?.variants['scaled-xlarge']?.url ||
                          images?.[0]?.attributes?.variants['listing-card']?.url
                        }
                        alt=""
                      />
                    </div>
                    <div className={css.productInfo}>
                      <h2>{title}</h2>
                      <p>{formattedPrice}</p>
                    </div>
                  </div>
                  <div className={css.stickyOrderFormEnd}>
                    <div className={css.stock}>
                      <InlineTextButton type="button">
                        <Icons name="minus" />
                      </InlineTextButton>
                      <span className={css.stockCount}>1</span>
                      <InlineTextButton type="button">
                        <Icons name="plus" />
                      </InlineTextButton>
                    </div>

                    <PrimaryButton className={css.addCartBtn}>Add to cart</PrimaryButton>
                  </div>
                </div>
              )} */}
              {/* <SimilarListingsSection intl={intl} listings={similarListings} /> */}
              {/* <PickedForYou intl={intl} listings={listings} /> */}
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const { currentPageResultIds = [] } = state.ListingPage;
  const listings = getListingsById(state, currentPageResultIds);
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    similarListingsIds = [],
  } = state.ListingPage;
  const { currentUser } = state.user;

  const similarListings = isArrayLength(similarListingsIds)
    ? getListingsById(state, similarListingsIds)
    : [];
  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    similarListings,
    listings,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport
)(EnhancedListingPage);

export default ListingPage;
